import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { CopyButton } from '../components/CopyButton';
import {
  ApiBuilder,
  type ApiBuilderState,
} from '../components/ApiBuilder';

export type SmsRequestType = 'GET' | 'POST';

export interface SmsWebhookFormValues {
  webhookName: string;
  smsText: string;
  payload: string;
  apiBuilder: ApiBuilderState;
}

interface SmsWebhookFormProps {
  mode: 'create' | 'update';
  initialValues: SmsWebhookFormValues;
  isPending: boolean;
  onSubmit: (payload: {
    webhookName: string;
    url: string;
    smsText: string;
    payload: string;
    requestType: SmsRequestType;
    header: string;
  }) => void;
}

function serializeHeaders(state: ApiBuilderState): string {
  if (state.headerMode === 'raw') return state.rawHeaders.trim();
  const obj = state.headers.reduce<Record<string, string>>(
    (acc, { key, value }) => {
      if (key.trim()) acc[key] = value ?? '';
      return acc;
    },
    {},
  );
  return Object.keys(obj).length === 0 ? '' : JSON.stringify(obj, null, 2);
}

export function SmsWebhookForm({
  mode,
  initialValues,
  isPending,
  onSubmit,
}: SmsWebhookFormProps) {
  const [webhookName, setWebhookName] = useState(initialValues.webhookName);
  const [smsText, setSmsText] = useState(initialValues.smsText);
  const [payload, setPayload] = useState(initialValues.payload);
  const [requestType, setRequestType] = useState<SmsRequestType | ''>(
    initialValues.apiBuilder.method as SmsRequestType,
  );
  const [builderState, setBuilderState] = useState<ApiBuilderState>(
    initialValues.apiBuilder,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleBuilderSubmit = (url: string, bodyString: string) => {
    setBuilderState((prev) => ({ ...prev, url }));
    if (requestType === 'POST') setPayload(bodyString);
    toast.success('URL applied to the webhook form');
  };

  const validate = (): string | null => {
    if (!webhookName.trim()) return 'Webhook name is required.';
    if (!builderState.url.trim()) return 'Set a webhook URL via the API Builder.';
    if (!requestType) return 'Pick a request type.';
    if (requestType === 'GET' && !smsText.trim()) return 'SMS text is required for GET.';
    if (requestType === 'POST' && !payload.trim())
      return 'Payload is required for POST.';
    return null;
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (!requestType) return;
    onSubmit({
      webhookName: webhookName.trim(),
      url: builderState.url.trim(),
      smsText: requestType === 'GET' ? smsText : '',
      payload: requestType === 'POST' ? payload : '',
      requestType,
      header: serializeHeaders(builderState),
    });
  };

  return (
    <ScrollPage>
      <PageHeader
        title={mode === 'create' ? 'Add SMS Webhook' : 'Update SMS Webhook'}
        description="Configure the HTTP call used to push SMS through your gateway."
        actions={
          <Button asChild variant="outline">
            <Link to="/sms-webhook-list">
              <ArrowLeft className="h-4 w-4" />
              Back to SMS webhooks
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={handleFormSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Webhook</CardTitle>
            <CardDescription>
              Name your gateway and pick the HTTP method it expects.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Webhook name</Label>
              <Input
                id="name"
                value={webhookName}
                onChange={(e) => setWebhookName(e.target.value)}
                autoFocus={mode === 'create'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="method">Request type</Label>
              <Select
                value={requestType}
                onValueChange={(v) => {
                  const m = v as SmsRequestType;
                  setRequestType(m);
                  setBuilderState((prev) => ({ ...prev, method: m }));
                }}
              >
                <SelectTrigger id="method">
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {requestType && (
          <ApiBuilder
            initial={builderState}
            method={requestType}
            onMethod={(m) => {
              setRequestType(m);
              setBuilderState((prev) => ({ ...prev, method: m }));
            }}
            onStateChange={setBuilderState}
            onSubmit={handleBuilderSubmit}
          />
        )}

        {requestType === 'GET' && (
          <Card>
            <CardHeader>
              <CardTitle>SMS Text</CardTitle>
              <CardDescription>
                Body text for the SMS — placeholders like{' '}
                <code className="font-mono text-xs">{'{PHONE}'}</code> are expanded by the
                platform.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                value={smsText}
                onChange={(e) => setSmsText(e.target.value)}
                rows={6}
                placeholder="Hello {PHONE}, your code is {CODE}."
              />
              <CopyButton text={smsText} />
            </CardContent>
          </Card>
        )}

        {requestType === 'POST' && (
          <Card>
            <CardHeader>
              <CardTitle>Payload</CardTitle>
              <CardDescription>
                Request body — replace the mobile number placeholder with{' '}
                <code className="font-mono text-xs">{'{PHONE}'}</code>. Click Submit in the
                API Builder above to copy the working body into this field.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                rows={6}
                placeholder={'{\n  "to": "{PHONE}",\n  "text": "…"\n}'}
                className="font-mono text-xs"
              />
              <CopyButton text={payload} />
            </CardContent>
          </Card>
        )}

        {serializeHeaders(builderState) && (
          <Card>
            <CardHeader>
              <CardTitle>Saved headers (JSON)</CardTitle>
              <CardDescription>
                These are the headers that will be sent with every delivery.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
                {serializeHeaders(builderState)}
              </pre>
              <CopyButton text={serializeHeaders(builderState)} />
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to="/sms-webhook-list">Cancel</Link>
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Webhook className="h-4 w-4" />
            )}
            {mode === 'create' ? 'Add SMS webhook' : 'Save changes'}
          </Button>
        </div>
      </form>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm submission</DialogTitle>
            <DialogDescription>
              Make sure the response payload your gateway returns is what you expect — once
              saved, this webhook will start firing immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              No, go back
            </Button>
            <Button onClick={handleConfirm}>Yes, save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollPage>
  );
}
